import { useEffect, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  useMapContext,
  ZoomableGroup,
} from 'react-simple-maps';
import countries from 'world-atlas/countries-50m.json';
import { teamLocations } from '../../../content/team';
import { useMessages } from '../../../i18n/locale';
import FadeInOnScroll from '../../../components/effects/FadeInOnScroll';
import BlurText from '../../../components/effects/BlurText';

const WORLD_VIEW = { center: [0, 0], zoom: 1 };
const CHINA_VIEW = { center: [104, 35], zoom: 4 };
const SKY_PATHS = [
  { city: 'beijing', bend: 0 },
  { city: 'anqing', bend: -8 },
  { city: 'hefei', bend: -5 },
  { city: 'hangzhou', bend: 0 },
  { city: 'suzhou', bend: 5 },
  { city: 'shanghai', bend: 8 },
  { city: 'nantong', bend: 10 },
];
const LOCATIONS_BY_ID = Object.fromEntries(
  teamLocations.map((location) => [location.id, location]),
);
const cityCoordinates = (id) => [LOCATIONS_BY_ID[id].longitude, LOCATIONS_BY_ID[id].latitude];

function SkyConnections() {
  const { projection } = useMapContext();
  const beijing = projection(cityCoordinates('beijing'));
  const shanghai = projection(cityCoordinates('shanghai'));
  if (!beijing || !shanghai) return null;

  const hub = [(beijing[0] + shanghai[0]) / 2 + 3, beijing[1] - 18];
  return (
    <g aria-hidden="true">
      {SKY_PATHS.map(({ city, bend }, index) => {
        const point = projection(cityCoordinates(city));
        if (!point) return null;
        const path = `M ${point[0]} ${point[1]} C ${point[0] + bend} ${point[1] - 14}, ${hub[0] + bend * 0.4} ${hub[1] + 16}, ${hub[0]} ${hub[1]}`;
        return (
          <g key={city}>
            <path className="team-map-connection" d={path} />
            <circle className="team-map-travel" r="0.4">
              <animateMotion
                path={path}
                dur="5s"
                begin={`${index * 0.48}s`}
                repeatCount="indefinite"
              />
            </circle>
          </g>
        );
      })}
      <circle className="team-map-hub" cx={hub[0]} cy={hub[1]} r="0.95" />
    </g>
  );
}
const LABEL_PLACEMENT = {
  anqing: { x: -11, y: 21, anchor: 'end' },
  hefei: { x: -11, y: 3, anchor: 'end' },
  nantong: { x: 11, y: -18, anchor: 'start' },
  suzhou: { x: 11, y: 1, anchor: 'start' },
  hangzhou: { x: -11, y: 39, anchor: 'end' },
  beijing: { x: 11, y: -10, anchor: 'start' },
  shanghai: { x: 11, y: 20, anchor: 'start' },
};

export default function TeamMapSection() {
  const copy = useMessages().home.teamMap;
  const [view, setView] = useState(CHINA_VIEW);
  const [compact, setCompact] = useState(
    () => typeof window !== 'undefined' && window.matchMedia?.('(max-width: 600px)').matches,
  );
  const chinaView = view.zoom > 1;
  const markerScale = (compact ? 1.65 : 1) / view.zoom;

  useEffect(() => {
    const media = window.matchMedia?.('(max-width: 600px)');
    if (!media) return;
    const update = () => setCompact(media.matches);
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return (
    <section id="team-map" className="team-map-section section-pad">
      <div className="container">
        <FadeInOnScroll className="team-map-heading">
          <h2>
            <BlurText text={copy.title[0]} />
            <br />
            <BlurText text={copy.title[1]} delay={140} />
          </h2>
          <p>{copy.description}</p>
        </FadeInOnScroll>
        <FadeInOnScroll className="team-map-visual" delay={100}>
          <ComposableMap
            width={1200}
            height={570}
            projection="geoEqualEarth"
            projectionConfig={{ scale: 194 }}
            role="group"
            aria-label={copy.mapLabel}
          >
            <ZoomableGroup
              center={view.center}
              zoom={view.zoom}
              minZoom={1}
              maxZoom={10}
              filterZoomEvent={(event) => event.type !== 'wheel'}
              onMoveEnd={({ coordinates, zoom }) => setView({ center: coordinates, zoom })}
            >
              <Geographies geography={countries}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      className={`team-map-land${geo.id === '156' ? ' team-map-land--china' : ''}`}
                    />
                  ))
                }
              </Geographies>
              {chinaView ? (
                <>
                  <SkyConnections />
                  {teamLocations.map((location) => (
                    <Marker
                      key={location.id}
                      coordinates={[location.longitude, location.latitude]}
                      className="team-map-city"
                      role="img"
                      aria-label={copy.cities[location.id]}
                    >
                      <circle className="team-map-city__dot" r={3.2 * markerScale} />
                      <text
                        className="team-map-city__label"
                        x={LABEL_PLACEMENT[location.id].x * markerScale}
                        y={LABEL_PLACEMENT[location.id].y * markerScale}
                        textAnchor={LABEL_PLACEMENT[location.id].anchor}
                        style={{
                          fontSize: `${12 * markerScale}px`,
                          strokeWidth: `${2.2 * markerScale}px`,
                        }}
                      >
                        {copy.cities[location.id]}
                      </text>
                    </Marker>
                  ))}
                </>
              ) : (
                <Marker
                  coordinates={[118.8, 34.2]}
                  className="team-map-cluster"
                  role="button"
                  tabIndex={0}
                  aria-label={copy.explore}
                  onClick={() => {
                    setView(CHINA_VIEW);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      setView(CHINA_VIEW);
                    }
                  }}
                >
                  <circle className="team-map-cluster__core" r="5" />
                  <text x="30" y="5">
                    {copy.cluster}
                  </text>
                </Marker>
              )}
            </ZoomableGroup>
          </ComposableMap>
          <div className="team-map-overlay">
            <button
              type="button"
              onClick={() => {
                setView(chinaView ? WORLD_VIEW : CHINA_VIEW);
              }}
            >
              {chinaView ? copy.back : copy.explore}
              <span aria-hidden="true">{chinaView ? '↗' : '↙'}</span>
            </button>
          </div>
        </FadeInOnScroll>
      </div>
    </section>
  );
}
